import classNames from "classnames";
import { X } from "lucide-react";
import * as React from "react";

import type { FilterDefinition } from "@/shared/components/ResourceView";
import { RobotoButton, useHotkey } from "@/shared/foundation/primitives";

import { CardConfig, SectionConfig } from "../DashboardHeader/DashboardParams";

import styles from "./WidgetEditor.module.css";
import { WidgetEditorMode } from "./widgetEditorMode";
import { WidgetFields } from "./WidgetFields";

export interface WidgetEditorProps {
  /** Which widget the URL is asking to work on. */
  mode: WidgetEditorMode;
  /** The widget being configured. Owned by the caller, which keeps it in the
   *  URL so a reload mid-edit comes back to the same half-built widget. */
  draft: CardConfig;
  /** Fired with the updated draft whenever the user changes a field. */
  onDraftChange: (next: CardConfig) => void;
  /** The section the widget joins — resolved by the caller against the
   *  effective definition, since only it knows the live sections. */
  section: SectionConfig;
  /** Every selectable metric name. */
  allNames: string[];
  /** The fields a chart may be split by; forwarded to the fields column,
   *  which hides its Group by picker when there are none. */
  groupableFields?: FilterDefinition[];
  /** The metric a card will actually render: its own, or the dashboard's
   *  fallback when unset. */
  resolveMetric: (card: CardConfig) => string;
  /** Render a card's body exactly as the dashboard does. Supplying the real
   *  renderer — rather than a preview-shaped lookalike — is what makes the
   *  preview truthful: same components, same time range, period, and filters
   *  as the cards behind it. */
  renderPreview: (card: CardConfig) => React.ReactNode;
  /** Commit the finished widget to the dashboard. */
  onCommit: () => void;
  /** Whether the commit's save is in flight. */
  isSaving?: boolean;
  /** Leave without committing: navigates back, discarding the draft. */
  onClose: () => void;
}

/**
 * The widget create/edit surface, rendered full-viewport over the dashboard.
 *
 * One room with two doors: `mode` says whether the user arrived to create a
 * widget or to reconfigure one, and below that distinction the surface is
 * identical. Fields on the left are the cause; the preview on the right is the
 * effect.
 *
 * It owns no widget state at all: the draft arrives as a prop and every field
 * change goes back out through `onDraftChange`, because the draft lives in
 * the URL. The layout, the localStorage draft, the save, and the conflict
 * handling all stay with `MetricsDashboard`, which is why this can be a
 * routed surface without a second owner of the save contract.
 */
export function WidgetEditor({
  mode,
  draft,
  onDraftChange,
  section,
  allNames,
  groupableFields,
  resolveMetric,
  renderPreview,
  onCommit,
  isSaving = false,
  onClose,
}: WidgetEditorProps) {
  const headingId = React.useId();

  // Enable escape key so it behaves functionally like a dialog
  useHotkey("Escape", onClose, { enabled: !isSaving });

  const isCreate = mode.kind === "create";
  const noun = draft.kind === "chart" ? "chart" : "trend";

  return (
    <div className={styles.scrim}>
      <div
        className={styles.surface}
        data-testid="widget-editor"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
      >
        <header className={styles.header}>
          <h1 className={styles.heading} id={headingId}>
            {isCreate ? `New ${noun}` : `Configure ${noun}`}{" "}
            <span className={styles.headingSubtitle}>in {section.title}</span>
          </h1>
          <RobotoButton
            variant="secondary"
            className={styles.closeButton}
            icon={<X aria-hidden="true" />}
            aria-label="Close widget editor"
            onClick={onClose}
          />
        </header>

        <div className={styles.panes}>
          <div className={styles.controls}>
            <WidgetFields
              card={draft}
              allNames={allNames}
              groupableFields={groupableFields}
              effectiveMetric={resolveMetric(draft)}
              onChange={onDraftChange}
            />
          </div>
          {/* The real card component, fed the draft. Sized to the footprint the
              widget will occupy so the preview is a rehearsal, not a portrait. */}
          <div className={styles.preview} data-testid="widget-editor-preview">
            <div
              className={classNames({
                [styles.previewTrend]: draft.kind === "trend",
              })}
            >
              {renderPreview(draft)}
            </div>
          </div>
        </div>

        <footer className={styles.footer} data-testid="widget-editor-footer">
          <RobotoButton
            variant="secondary"
            disabled={isSaving}
            onClick={onClose}
          >
            Cancel
          </RobotoButton>
          <RobotoButton variant="primary" loading={isSaving} onClick={onCommit}>
            {isCreate ? `Add to ${section.title}` : "Save changes"}
          </RobotoButton>
        </footer>
      </div>
    </div>
  );
}
